/* eslint-disable arrow-body-style */
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { map, switchMap, take, withLatestFrom } from 'rxjs/operators';

import { env } from '@amfam/environment';
import {
  TemplatedEmail,
  type CustomerNotificationSuccessModel
} from '@amfam/policy/models/lib/models/replace-vehicle';
import { selectMdmId, selectWaid } from '@amfam/shared/user/lib/+state/user.selectors';
import { selectCustomerGroupId } from '@amfam/shared/utility/brand/lib/+state/brand.selectors';
import { CommunicationService } from '@amfam/shared/utility/shared-services/lib/communication/communication.service';

import {
  DeleteMyAccountResponse,
  UnenrollProgramsResponse
} from '../+state/delete-myaccount-state/delete-myaccount.model';
import { userDetails } from '../models/delete.model';

@Injectable()
export class DeleteService {
  private store = inject(Store);
  private http = inject(HttpClient);
  private communicationService = inject(CommunicationService);

  private deleteAccountApiUrl: string;
  private unenrollUrl: string;
  private deleteUrl: string;
  private appName: string;
  private deleteCiamUrl: string;

  constructor() {
    this.appName = env.applicationTitle;
    this.deleteAccountApiUrl = env.userAccountApi;
    this.unenrollUrl = this.deleteAccountApiUrl + '/unenrollprograms';
    this.deleteUrl = this.deleteAccountApiUrl + '/delete';
    this.deleteCiamUrl = this.deleteAccountApiUrl + '/delete/ciam';
  }

  unenrollPrograms(): Observable<UnenrollProgramsResponse> {
    const unenrollUrl =
      this.appName === 'MyAccountAdmin'
        ? this.unenrollUrl + '/' + this.retreiveWaid()
        : this.unenrollUrl;
    return this.http.post<UnenrollProgramsResponse>(unenrollUrl, null).pipe(
      map(data => {
        return data;
      })
    );
  }

  deleteMyaccount(): Observable<DeleteMyAccountResponse> {
    let deleteUrl;
    let body;
    if (this.appName === 'MyAccountAdmin') {
      body = {
        reason: 'Delete MyAccount - Agent initiated via Web app'
      };
      deleteUrl = this.deleteUrl + '/' + this.retreiveWaid();
    } else {
      body = {
        reason: 'Delete MyAccount - User initiated via Web app'
      };
      deleteUrl = this.deleteUrl;
    }
    return this.http.post<DeleteMyAccountResponse>(deleteUrl, body).pipe(
      map(data => {
        return data;
      })
    );
  }

  deleteDuplicateAccount(forgeRockId: string | undefined, partnerId: string, groupId?: string) {
    let headers: HttpHeaders = new HttpHeaders();
    headers = headers.set('afe-partner-id', partnerId);
    const body = {
      reason: 'Delete MyAccount - Agent initiated via Web app'
    };
    const params = new HttpParams().set('customerGroupId', groupId ?? '');
    return this.http.post<void>(
      `${this.deleteCiamUrl}/${encodeURIComponent(forgeRockId ?? '')}`,
      body,
      { headers, params }
    );
  }

  sendTemplatedEmails(payload: userDetails): Observable<CustomerNotificationSuccessModel> {
    const messageDetails = [
      {
        key: 'EmailAddress',
        value: ''
      },
      {
        key: 'SubscriberKey',
        value: ''
      },
      {
        key: 'FirstName',
        value: payload.firstName
      }
    ];

    return this.store.select(selectMdmId).pipe(
      take(1),
      withLatestFrom(this.store.select(selectCustomerGroupId)),
      switchMap(([mdmId, customerGroupId]) => {
        const customerIdValue = this.appName === 'MyAccountAdmin' && mdmId ? mdmId : 'CDHID-CTDNC';
        // We need to keep separate template codes because emails are triggered differently
        // for partners under 642, the email is triggered from the Agency BU, while for non-642 partners (e.g., Lemonade),
        // the emails are triggered from the Partner BU.
        const templateCode = customerGroupId === '642' ? 'MYAC_DEL' : 'DeleteMyAccount';
        const email = new TemplatedEmail(
          templateCode,
          undefined,
          'MyAccount',
          'EN',
          customerIdValue,
          payload.email,
          messageDetails
        );
        return this.store.select(selectCustomerGroupId).pipe(
          switchMap(cId => {
            // In future we will replace the Customer Group ID number from 642 to AFCG.
            // For now, we will have both available as a brand check.
            if (!['642', 'AFCG'].includes(cId)) email.partnerId = cId;
            return this.communicationService.sendTemplatedEmail(email);
          })
        );
      })
    );
  }

  private retreiveWaid(): string {
    return this.store.selectSignal(selectWaid)() ?? '';
  }
}
